import React, { useCallback, useMemo } from 'react';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import ListItem from '@tiptap/extension-list-item';
import { mergeAttributes } from '@tiptap/core';
import { Plugin, NodeSelection } from 'prosemirror-state';
import { sinkListItem, liftListItem } from 'prosemirror-schema-list';
import { FaTrash, FaGripVertical } from 'react-icons/fa';

// Max number of list ancestors (`ol`/`ul`) the cursor may sit inside.
// `1 → a → i` is depth 3. Beyond that the outline becomes unreadable, so
// Tab on a depth-3 item is silently consumed.
const MAX_LIST_NESTING_DEPTH = 3;

// React.memo is intentionally NOT used: the layout switches between
// hug-content and stable-width based on whether the item is *nested*, and
// memo's shallow `node` compare misses pure position changes (e.g. a sink
// that moves the item into a new `ol` without changing its content).
const DraggableListItemView = ({ node, editor, getPos }) => {
  const handleDelete = useCallback(() => {
    if (!editor || !getPos) return;
    try {
      const pos = getPos();
      if (typeof pos !== 'number') return;
      const { doc } = editor.state;
      const end = pos + node.nodeSize;
      if (pos < 0 || end > doc.content.size) return;
      editor.chain()
        .focus()
        .setTextSelection(pos)
        .deleteRange({ from: pos, to: end })
        .run();
    } catch (_) {
      /* position may be stale after doc change; ignore */
    }
  }, [editor, getPos, node.nodeSize]);

  const liClass = useMemo(
    () => ["draggable-list-item", node.attrs.class].filter(Boolean).join(" "),
    [node.attrs.class],
  );

  /* Row + content: always full width of the <li> (RTEStyles.css also sets flex on
   * this div — keep Tailwind aligned so the prose block uses the real line width,
   * not a shrink-wrapped "text + icons" column that causes early wraps / yo-yo). */
  const rowClass =
    "group relative z-0 flex w-full min-w-0 max-w-full items-center gap-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/60 px-1 -mx-1";

  const contentClass =
    "min-w-0 flex-1 [&>*]:m-0 [&>*]:max-w-full [&>*]:min-w-0 cursor-text";

  return (
    <NodeViewWrapper
      as="li"
      className={liClass}
      onDragStart={(e) => {
        // Only allow drag if starting from drag handle (same behavior as paragraphs)
        if (!e.target.closest("[data-drag-handle]")) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className={rowClass}>
        <NodeViewContent
          className={contentClass}
          onDragStart={(e) => {
            // Block ad-hoc text drags from inside this item, BUT allow drags
            // that start from a nested item's drag handle to pass through —
            // a nested list item lives inside this item's NodeViewContent,
            // so its `dragstart` bubbles here and would otherwise be killed.
            if (e.target.closest && e.target.closest("[data-drag-handle]")) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
          }}
        />
        <div className="relative z-10 flex w-14 flex-shrink-0 items-center justify-end gap-1 ps-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="rounded p-1 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            title="Delete"
            contentEditable={false}
          >
            <FaTrash className="w-3 h-3" />
          </button>
          <div
            data-drag-handle
            draggable="true"
            className="rounded p-1 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 cursor-grab active:cursor-grabbing"
            title="Drag"
            contentEditable={false}
            onMouseDown={() => {
              // Select THIS list item as a NodeSelection BEFORE the native
              // `dragstart` fires. ProseMirror's dragstart handler falls
              // back to `view.state.selection` when it can't resolve a node
              // at the click coords (which is the case for nested items —
              // the cursor lands inside the paragraph, not on the listItem).
              // Setting the selection on mousedown guarantees the right
              // listItem is serialized into the drag slice.
              if (!editor || !getPos) return;
              try {
                const pos = getPos();
                if (typeof pos !== 'number') return;
                const $pos = editor.state.doc.resolve(pos);
                if ($pos.nodeAfter && $pos.nodeAfter.type.name === 'listItem') {
                  const tr = editor.state.tr.setSelection(
                    NodeSelection.create(editor.state.doc, pos)
                  );
                  editor.view.dispatch(tr);
                }
              } catch (_) {
                /* stale getPos — let PM use its default selection */
              }
            }}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
            }}
          >
            <FaGripVertical className="w-3 h-3" />
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export const DraggableListItem = ListItem.extend({
  priority: 1000,
  draggable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class") || null,
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "li",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        ...(node.attrs.class ? { class: node.attrs.class } : {}),
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DraggableListItemView);
  },

  // Keep the parent's keyboard shortcuts (notably `Enter` → splitListItem).
  // Tab and Shift-Tab are deliberately handled by the `handleKeyDown`
  // ProseMirror plugin below — that fires before any keymap and gives us
  // a hard guarantee that the depth cap is enforced and the event is
  // consumed (no focus-shift out of the editor) on every keypress.
  addKeyboardShortcuts() {
    return this.parent?.() || {};
  },

  addProseMirrorPlugins() {
    const itemName = this.name;

    return [
      // Tab/Shift-Tab in list items: indent (sink) / outdent (lift) with
      // a hard cap at MAX_LIST_NESTING_DEPTH levels of nesting. Implemented
      // as a `handleKeyDown` plugin instead of `addKeyboardShortcuts` so it
      // runs *before* every other extension's keymap and is unaffected by
      // HMR weirdness around extension reload order.
      new Plugin({
        props: {
          handleKeyDown(view, event) {
            if (event.key !== 'Tab') return false;
            if (event.ctrlKey || event.metaKey || event.altKey) return false;

            const { state } = view;
            const { $from } = state.selection;

            // Cursor inside a listItem?
            let inListItem = false;
            for (let d = $from.depth; d > 0; d--) {
              if ($from.node(d).type.name === itemName) {
                inListItem = true;
                break;
              }
            }
            if (!inListItem) return false; // outside list — let event propagate

            event.preventDefault();

            const itemType = state.schema.nodes[itemName];
            if (!itemType) return true;

            if (event.shiftKey) {
              liftListItem(itemType)(state, view.dispatch);
              return true;
            }

            // Count list ancestors. `1 → a → i` is depth 3.
            let listDepth = 0;
            for (let d = $from.depth; d > 0; d--) {
              const name = $from.node(d).type.name;
              if (name === 'orderedList' || name === 'bulletList') listDepth++;
            }
            if (listDepth >= MAX_LIST_NESTING_DEPTH) {
              // Already at max — consume the Tab so focus doesn't leave the
              // editor, but don't sink any deeper.
              return true;
            }

            sinkListItem(itemType)(state, view.dispatch);
            return true;
          },
        },
      }),
    ];
  },
});
